import type { Country, Zone } from '@seek/audience-zones';
import type { Brand, Language } from '@seek/ca-myprofile-utils';

import {
  CREATE_RESUME_PAGE,
  PROFILE_PAGE,
  SHAREABLE_PROFILE_PAGE,
} from 'src/components/Router';
import type { ADD, EDIT, DELETE, REJECT } from 'src/consts';

export type AnalyticsBrand = Lowercase<Brand>;

export interface AnalyticsState {
  currentPage?: AnalyticsPage;
  loginId?: string;
  isLoggedIn?: boolean;
  actionOrigin?: AnalyticsActionOrigin;
  profileGuid?: string;
  legacyCandidateId?: string;
  siteCountry?: Lowercase<Country>;
  brand?: AnalyticsBrand;
  zone?: Zone;
  profileVisibilityLevel?: AnalyticsProfileVisibilityLevel;
  siteLanguage?: Language;
  experiments?: AnalyticsExperiments[];
  profileApproachabilityStatus?: AnalyticsProfileApproachabilityStatus;
  profileSharedStatus?: AnalyticsProfileSharedStatus;
  isUnifiedSite?: boolean;
  profileViewedCount?: number;
  profileSearchAppearanceCount?: number;
}

export interface Store {
  getState: () => AnalyticsState;
}

export type ConfirmedActionName = typeof ADD | typeof EDIT | typeof DELETE;
export type UnconfirmedActionName = typeof ADD | typeof EDIT | typeof REJECT;

const UPDATE_TYPE = ['add', 'edit', 'delete'] as const;
export type AnalyticsUpdateType = (typeof UPDATE_TYPE)[number];

const ADD_TYPE = ['add', 'edit'] as const;
export type AnalyticsAddType = (typeof ADD_TYPE)[number];

const CURRENT_PAGE_SECTION_STATE = ['add', 'edit', 'view'] as const;
export type AnalyticsCurrentPageSectionState =
  (typeof CURRENT_PAGE_SECTION_STATE)[number];

const PROFILE_VISIBILITY_LEVEL = [
  'standard',
  'limited',
  'hidden',
  'not set',
] as const;
export type AnalyticsProfileVisibilityLevel =
  (typeof PROFILE_VISIBILITY_LEVEL)[number];

const PROFILE_APPROACHABILITY_STATUS = ['on', 'off'] as const;
export type AnalyticsProfileApproachabilityStatus =
  (typeof PROFILE_APPROACHABILITY_STATUS)[number];

const PROFILE_SHARED_STATUS = ['on', 'off', 'not generated'] as const;
export type AnalyticsProfileSharedStatus =
  (typeof PROFILE_SHARED_STATUS)[number];

const ACTION_ORIGIN = [
  'main card',
  'resume parsing nudge',
  'top nudge',
  'application form',
  'pop up',
  'manage resumes',
  'generate resume prompt',
  'go1',
  'profile',
  'centralised nudge',
] as const;

export type AnalyticsActionOrigin = (typeof ACTION_ORIGIN)[number];

const RESUME_TYPE = ['generated', 'uploaded'] as const;
export type AnalyticsResumeType = (typeof RESUME_TYPE)[number];

export interface AnalyticsViewData {
  eventName: 'profile_displayed';
  currentPage: 'profile';
}

export type ManageResumeEventName =
  | 'resume_download_succeeded'
  | 'resume_make_default_succeeded'
  | 'resume_parsing_succeeded'
  | 'resume_upload_pressed'
  | 'profile_update_succeeded';

export interface ManageResumeUpdateProps {
  currentPageSection: 'resume';
  eventName: ManageResumeEventName;
  isResumeDefault?: boolean;
  resumeType?: AnalyticsResumeType;
  updateType?: AnalyticsUpdateType;
  actionOrigin?: AnalyticsActionOrigin;
  linkPosition?: AnalyticsActionOrigin;
  parsedData?: Array<{
    document: string;
    section: string;
    info: string;
    count: number;
  }>;
}

export interface CreateResumeDisplayed {
  currentPage: 'create resume';
  eventName: 'create_resume_displayed';
}

export interface AnalyticsService {
  view: (viewName: AnalyticsViewEvent, data: Record<string, unknown>) => void;
  link: (linkName: AnalyticsLinkEvent, data: Record<string, unknown>) => void;
}

const VERIFICATION_STATUSES = [
  'verified',
  'not-verified',
  'unverifiable',
  'none',
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export interface AnalyticsLicenceInfo {
  verificationStatus: VerificationStatus;
  isExpired: boolean;
}

export interface AnalyticsQualificationVerifiedInfo {
  qualificationVerificationStatus: VerificationStatus;
}

export const MAIN_PAGE = {
  [PROFILE_PAGE]: 'profile',
  [SHAREABLE_PROFILE_PAGE]: 'shareable profile',
  [CREATE_RESUME_PAGE]: 'create resume',
} as const;

export const PAGE = [
  'profile',
  'shareable profile',
  'create resume',
  'profile visibility shared profile lite consent',
  'profile talent search insights view',
] as const;
export type AnalyticsPage = (typeof PAGE)[number];

const CURRENT_PAGE_SECTION = [
  'personal details',
  'personal summary',
  'visibility settings',
  'talent search insights',
  'career history',
  'education',
  'licence',
  'resume',
  'skills',
  'language',
  'next role',
  'years of experience', // JobsDB only
  'verifications',
] as const;

export type AnalyticsCurrentPageSection = (typeof CURRENT_PAGE_SECTION)[number];

const SUGGESTIONS_NUDGE_TYPES = [
  'personal_details',
  'recent_role',
  'profile_visibility',
  'resume',
  'career_history',
  'another_role',
  'education',
  'salary',
  'work_rights',
  'skills',
  'licence_or_certification',
  'language',
  'summary',
  'availability',
  'classification',
  'work_preference',
] as const;

export type AnalyticsSuggestionsNudgeType =
  (typeof SUGGESTIONS_NUDGE_TYPES)[number];

const SUGGESTIONS_CALL_TO_ACTION_TYPES = [
  'add_personal_details',
  'add_most_recent_role',
  'set_profile_visibility',
  'add_resume',
  'complete_work_history',
  'add_another_role',
  'add_education',
  'add_salary',
  'add_work_rights',
  'add_5_or_more_skills',
  'add_licence_or_certificate',
  'add_language',
  'add_summary',
  'add_availability',
  'add_classification',
  'add_work_preference',
] as const;

export type AnalyticsSuggestionsCallToActionType =
  (typeof SUGGESTIONS_CALL_TO_ACTION_TYPES)[number];

// VIEW_EVENT is for events ending in `_displayed` and are always sent with `view` function, and LINK_EVENT is for everything else and always sent with `link` function
const ANALYTICS_VIEW_EVENT = [
  'profile_displayed',
  'create_resume_displayed',
  'profile_section_displayed',
  'profile_link_share_prompt_displayed',
  'certsy_dialog_displayed',
] as const;
export type AnalyticsViewEvent = (typeof ANALYTICS_VIEW_EVENT)[number];

const ANALYTICS_LINK_EVENT = [
  'certsy_dialog_pressed',
  'certsy_in_review_pressed',
  'certsy_manage_pressed',
  'certsy_learn_more_pressed',
  'certsy_verify_pressed',
  'company_reviews_pressed',
  'create_resume_displayed',
  'create_resume_pressed',
  'profile_add_country_pressed',
  'delete_right_to_work_pressed',
  'external_link_pressed',
  'navigation_pressed',
  'nudge_viewed',
  'nudge_pressed',
  'nudge_dismiss_pressed',
  'opportunity_viewed',
  'profile_available_verifications_tab_pressed',
  'profile_insights_widget_loaded',
  'profile_insights_widget_pressed',
  'profile_insights_activity_pressed',
  'profile_link_copy_pressed',
  'profile_link_open_pressed',
  'profile_section_displayed',
  'profile_update_succeeded',
  'profile_verifications_widget_viewed',
  'profile_verifications_widget_pressed',
  'profile_your_verifications_tab_pressed',
  'resume_delete_pressed',
  'resume_download_succeeded',
  'resume_make_default_succeeded',
  'resume_parsing_new_info_pressed',
  'resume_parsing_succeeded',
  'resume_upload_pressed',
] as const;

export type AnalyticsLinkEvent = (typeof ANALYTICS_LINK_EVENT)[number];

const ANALYTICS_NUDGE_TYPE = [
  'default_resume_standard_visibility',
  'default_resume_limited_visibility',
  'default_resume_hidden_visibility',
  'home_location',
  'preferred_location',
  'right_to_work',
] as const;
export type AnalyticsNudgeType = (typeof ANALYTICS_NUDGE_TYPE)[number];

const ANALYTICS_CALL_TO_ACTION = [
  'set_default_resume',
  'set_home_location',
  'set_preferred_location',
  'set_right_to_work',
] as const;
export type AnalyticsCallToAction = (typeof ANALYTICS_CALL_TO_ACTION)[number];

const ANALYTICS_CALL_TO_ACTION_TYPE = ['nudge'] as const;
export type AnalyticsCallToActionType =
  (typeof ANALYTICS_CALL_TO_ACTION_TYPE)[number];

const CURRENT_PAGE_SUBSECTION = [
  'availability',
  'work type',
  'location',
  'right to work',
  'salary',
  'classification',
  'approachability',
  'shared profile',
  'visibility',
  'verifications',
] as const;

export type AnalyticsCurrentPageSubsection =
  (typeof CURRENT_PAGE_SUBSECTION)[number];

const CURRENCY_CODES = ['AUD', 'NZD', 'GBP', 'HKD', 'IDR', 'THB'] as const;
export type AnalyticsCurrencyCode = (typeof CURRENCY_CODES)[number];

const LINK_POSITION = ['main card', 'pop up'] as const;
export type AnalyticsLinkPosition = (typeof LINK_POSITION)[number];

const OPPORTUNITY_TYPES = ['suggested skills'] as const;
export type OpportunityType = (typeof OPPORTUNITY_TYPES)[number];

export interface AnalyticsHeaderFooterLinkProps {
  linkText: string;
  linkAction?: 'expand' | 'collapse';
  linkPosition?: 'header' | 'footer' | 'expanded menu';
}

export type AnalyticsCountryName = (typeof LINK_POSITION)[number];

export interface AnalyticsExperiments {
  id: string;
  num: number;
  variant?: '0' | '1' | '2';
}

export interface AnalyticsProfileInsightsProps {
  currentPage?: AnalyticsPage;
  currentPageSection?: AnalyticsCurrentPageSection;
  currentPageSectionState?: AnalyticsCurrentPageSectionState;
}

export interface AnalyticsProfileSuggestionsProps {
  currentPage?: AnalyticsPage;
  nudgeType?: AnalyticsSuggestionsNudgeType | string;
  nudgeCount?: number;
  callToActionType?: AnalyticsCallToActionType;
  callToAction?: AnalyticsSuggestionsCallToActionType;
  nudgePosition?: number;
}

const VERIFIABLE_CREDENTIAL_TYPES = [
  'identity',
  'workplace',
  'licence',
] as const;
export type AnalyticsVerifiableCredentialType =
  (typeof VERIFIABLE_CREDENTIAL_TYPES)[number];

const VERIFICATIONS_WIDGET_NAMES = [
  'verifications',
  'identity',
  'workplace',
] as const;
export type AnalyticsVerificationsWidgetName =
  (typeof VERIFICATIONS_WIDGET_NAMES)[number];

const ANALYTICS_VERIFICATIONS_WIDGET_CALL_TO_ACTION = [
  'view_verifications',
  'add_verifications',
  'add_verification_workplace',
  'add_verification_identity',
] as const;
export type AnalyticsVerificationsWidgetCallToAction =
  (typeof ANALYTICS_VERIFICATIONS_WIDGET_CALL_TO_ACTION)[number];

const ANALYTICS_VERIFICATIONS_WIDGET_LINK_TEXT = [
  'view all',
  'get verified',
  'verify your workplace',
  'verify your identity',
] as const;
export type AnalyticsVerificationsWidgetLinkText =
  (typeof ANALYTICS_VERIFICATIONS_WIDGET_LINK_TEXT)[number];

const ANALYTICS_VERIFICATIONS_CURRENT_PAGE_SUBSECTION = [
  'your verifications',
  'available verifications',
] as const;
export type AnalyticsVerificationsCurrentPageSubsection =
  (typeof ANALYTICS_VERIFICATIONS_CURRENT_PAGE_SUBSECTION)[number];

const ANALYTICS_VERIFICATIONS_SEEK_PASS_DIALOG_CALL_TO_ACTION = [
  'verify on certsy',
  'maybe later',
] as const;
export type AnalyticsVerificationsSeekPassDialogCallToAction =
  (typeof ANALYTICS_VERIFICATIONS_SEEK_PASS_DIALOG_CALL_TO_ACTION)[number];
